import { forwardRef, memo } from 'react';
import classnames from 'clsx';
import { useIntl } from 'react-intl';

import { TextType } from './text-type';
import style from './text.module.less';

import type { TextElementTag, TextProps, RequiredTextValue } from './text-types';
import type { MessageDescriptor } from '../../../prop-types/intl-types';

type TextTypeMapEntry = {
  className: string;
  Element: TextElementTag;
};

const textTypeMap: Record<string, TextTypeMapEntry> = {
  [TextType.H1]: {
    className: style.h1,
    Element: 'h1',
  },
  [TextType.H2]: {
    className: style.h2,
    Element: 'h2',
  },
  [TextType.H3]: {
    className: style.h3,
    Element: 'h3',
  },
  [TextType.H4]: {
    className: style.h4,
    Element: 'h4',
  },
  [TextType.Label]: {
    className: style.label,
    Element: 'label',
  },
  [TextType.Description]: {
    className: style.description,
    Element: 'p',
  },
  [TextType.Paragraph]: {
    className: style.paragraph,
    Element: 'p',
  },
  [TextType.Caption]: {
    className: style.caption,
    Element: 'p',
  },
};

const defaultElement: TextTypeMapEntry = {
  className: '',
  Element: 'span',
};

const isMessageDescriptor = (value: RequiredTextValue): value is MessageDescriptor => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return 'id' in value && 'defaultMessage' in value && typeof value.id === 'string' && typeof value.defaultMessage === 'string';
};

type TextElementProps = {
  Tag: TextElementTag;
  children: React.ReactNode;
  className?: string;
  textTypeClassName?: string;
  shouldTruncate?: boolean;
  isBold?: boolean;
} & Omit<React.ComponentPropsWithoutRef<'span'>, 'children' | 'className'>;

const TextElement = forwardRef<HTMLElement, TextElementProps>(
  ({ Tag, children, className, shouldTruncate, textTypeClassName, isBold, ...rest }, ref) => {
    const Component = Tag as React.ElementType;

    return (
      <Component
        ref={ref}
        title={shouldTruncate && typeof children === 'string' ? children : undefined}
        {...rest}
        className={classnames(textTypeClassName, className, {
          [style.truncate]: shouldTruncate,
          [style.bold]: isBold,
        })}
      >
        {children}
      </Component>
    );
  },
);

TextElement.displayName = 'TextElement';

/**
 * @deprecated Use Mantine's `Title` (for H1–H4) or `Text` (for Paragraph/Description/Caption/Label) from `@mantine/core` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export const Text = memo(
  forwardRef<HTMLElement, TextProps>(
    ({ className, textType, value, formattedMessageValues, element, shouldTruncate = false, isBold, ...rest }, ref) => {
      const intl = useIntl();
      const { className: textTypeClassName, Element } = textTypeMap[textType] || defaultElement;

      const content = (isMessageDescriptor(value) ? intl.formatMessage(value, formattedMessageValues) : value) as React.ReactNode;

      return (
        <TextElement
          ref={ref}
          className={className}
          isBold={isBold}
          shouldTruncate={shouldTruncate}
          Tag={element || Element}
          textTypeClassName={textTypeClassName}
          {...rest}
        >
          {content}
        </TextElement>
      );
    },
  ),
);

Text.displayName = 'Text';
